import React from 'react';
import { ArrowDownLeft, Calendar, Layout } from 'lucide-react';
import { cn } from '../lib/utils';

interface JournalReportProps {
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  manualJournals: any[];
  accounts: any[];
  users: any[];
  journalReportColumns: string[];
  setJournalReportColumns: (cols: string[]) => void;
  setTempColumns: (cols: string[]) => void;
  setIsReorderModalOpen: (open: boolean) => void;
  setActiveReport: (report: string | null) => void;
}

export const JournalReport: React.FC<JournalReportProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  manualJournals,
  accounts,
  users,
  journalReportColumns,
  setJournalReportColumns,
  setTempColumns,
  setIsReorderModalOpen,
  setActiveReport
}) => {
  const filteredJournals = manualJournals
    .filter(j => j.date >= startDate && j.date <= endDate)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const renderJournalCell = (column: string, journal: any, line: any, account: any, postedByUser: any) => {
    switch (column) {
      case 'Date':
        return <td key={column} className="p-4 text-xs">{new Date(journal.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>;
      case 'Journal ID':
        return <td key={column} className="p-4 text-xs">{journal.journalNumber}</td>;
      case 'Account Code':
        return <td key={column} className="p-4 text-xs">{account?.code || '-'}</td>;
      case 'Account':
        return <td key={column} className="p-4 text-xs">{account?.name || 'Unknown Account'}</td>;
      case 'Debit':
        return <td key={column} className="p-4 text-right font-mono text-xs">{line.debit > 0 ? line.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>;
      case 'Credit':
        return <td key={column} className="p-4 text-right font-mono text-xs">{line.credit > 0 ? line.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>;
      case 'Posted Date':
        return (
          <td key={column} className="p-4 text-xs">
            {journal.postedAt ? (
              journal.postedAt.toDate ? journal.postedAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 
              new Date(journal.postedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            ) : '-'}
          </td>
        );
      case 'Posted By':
        return <td key={column} className="p-4 text-xs">{postedByUser?.displayName || postedByUser?.name || 'System'}</td>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Date range:</span>
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <button 
            onClick={() => {
              setTempColumns([...journalReportColumns]);
              setIsReorderModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
          >
            <Layout className="w-4 h-4" />
            Reorder columns
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Journal Report</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            Demo Company (Global)
          </p>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
            For the period {new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                {journalReportColumns.map(col => (
                  <th key={col} className={cn(
                    "p-4 text-[10px] uppercase font-bold tracking-widest opacity-40",
                    (col === 'Debit' || col === 'Credit') && "text-right"
                  )}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredJournals.map((journal) => {
                const postedByUser = users.find(u => u.uid === journal.postedBy || u.id === journal.postedBy);
                const totalDebit = (journal.lines || []).reduce((sum: number, line: any) => sum + (line.debit || 0), 0);
                const totalCredit = (journal.lines || []).reduce((sum: number, line: any) => sum + (line.credit || 0), 0);

                return (
                  <React.Fragment key={journal.id}>
                    <tr className="border-t border-[#141414]">
                      <td colSpan={journalReportColumns.length} className="p-4 font-bold text-sm">
                        {journal.isReversal ? `ID ${journal.journalNumber} Reversed: ` : `ID ${journal.journalNumber} `}
                        {journal.narration}
                      </td>
                    </tr>
                    {(journal.lines || []).map((line: any, idx: number) => {
                      const account = accounts.find(a => a.id === line.accountId || a.code === line.accountId);
                      return (
                        <tr key={idx} className="hover:bg-[#141414]/5 transition-colors border-t border-[#141414]/5">
                          {journalReportColumns.map(col => renderJournalCell(col, journal, line, account, postedByUser))}
                        </tr>
                      );
                    })}
                    <tr className="font-bold border-t border-[#141414]/20">
                      {journalReportColumns.map((col, idx) => {
                        if (idx === 0) return <td key={col} className="p-4 text-xs">Total</td>;
                        if (col === 'Debit') return <td key={col} className="p-4 text-right font-mono text-xs">{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>;
                        if (col === 'Credit') return <td key={col} className="p-4 text-right font-mono text-xs">{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>;
                        return <td key={col}></td>;
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}
              {filteredJournals.length === 0 && (
                <tr>
                  <td colSpan={journalReportColumns.length} className="p-12 text-center opacity-30 italic serif">
                    No journals found for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
